/**
 * Session inactivity utilities
 *
 * Plain (unencrypted) localStorage helpers for tracking the last time a user
 * interacted with the application. The activity timestamp is not sensitive data
 * so it does not need the AES-GCM encryption used in secure-storage.ts.
 */

import { LAST_ACTIVITY_STORAGE_KEY } from './session.config'

/**
 * Persist the current timestamp as the most recent user activity.
 * Call this on every meaningful user interaction (DOM events, route changes, saves).
 */
export function updateLastActivity(): void {
  localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString())
}

/**
 * Returns the stored last-activity timestamp (ms since epoch), or null if none exists.
 */
export function getLastActivityTimestamp(): number | null {
  const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)
  if (!raw) return null
  const ts = parseInt(raw, 10)
  return isNaN(ts) ? null : ts
}

/**
 * Remove the last-activity entry from localStorage (called on logout).
 */
export function clearLastActivity(): void {
  localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY)
}

/**
 * Returns true when the elapsed time since the last recorded activity
 * exceeds `timeoutMs`. Returns false when no activity timestamp exists
 * (fresh session — not yet expired).
 *
 * @param timeoutMs - Inactivity threshold in milliseconds (e.g. INACTIVITY_TIMEOUT_MS)
 */
export function isInactivityExpired(timeoutMs: number): boolean {
  const lastActivity = getLastActivityTimestamp()
  if (lastActivity === null) return false
  return Date.now() - lastActivity >= timeoutMs
}
